export type ImageLightFileKind = 'static' | 'dynamic' | 'unsupported'

export type ImageLightFileErrorKey =
  | 'image_light_static_type_error'
  | 'image_light_dynamic_type_error'
  | 'image_light_static_too_large'
  | 'image_light_dynamic_too_large'

export interface ImageLightFileValidationError {
  errorKey: ImageLightFileErrorKey
  context: string
  detail: {
    name: string
    type?: string
    size?: number
    maxSize?: number
  }
}

export const imageLightStaticTypes = ['image/png', 'image/jpeg', 'image/webp']
export const imageLightDynamicTypes = ['image/gif', 'image/webp']
export const imageLightMaxStaticFileSizeBytes = 10 * 1024 * 1024
export const imageLightMaxDynamicFileSizeBytes = 8 * 1024 * 1024
export const imageLightMaxFrameCount = 300
export const imageLightStaticMaxSizeMB = imageLightMaxStaticFileSizeBytes / 1024 / 1024
export const imageLightDynamicMaxSizeMB = imageLightMaxDynamicFileSizeBytes / 1024 / 1024

export const isImageLightStaticFile = (file: File) => imageLightStaticTypes.includes(file.type)
export const isImageLightDynamicFile = (file: File) => imageLightDynamicTypes.includes(file.type)

const isAnimatedWebP = async (file: File) => {
  if (file.type !== 'image/webp') return false
  const bytes = new Uint8Array(await file.arrayBuffer())
  for (let index = 12; index < bytes.length - 3; index++) {
    if (bytes[index] === 0x41 && bytes[index + 1] === 0x4e && bytes[index + 2] === 0x49 && bytes[index + 3] === 0x4d) {
      return true
    }
  }
  return false
}

export const resolveImageLightFileKind = async (file: File): Promise<ImageLightFileKind> => {
  if (file.type === 'image/gif') return 'dynamic'
  if (file.type === 'image/webp' && (await isAnimatedWebP(file))) return 'dynamic'
  return isImageLightStaticFile(file) ? 'static' : 'unsupported'
}

export const validateImageLightStaticFile = (file: File): ImageLightFileValidationError | null => {
  if (!isImageLightStaticFile(file)) {
    return {
      errorKey: 'image_light_static_type_error',
      context: 'unsupported static image type',
      detail: { name: file.name, type: file.type },
    }
  }

  if (file.size > imageLightMaxStaticFileSizeBytes) {
    return {
      errorKey: 'image_light_static_too_large',
      context: 'static image file is too large',
      detail: { name: file.name, size: file.size, maxSize: imageLightMaxStaticFileSizeBytes },
    }
  }

  return null
}

export const validateImageLightDynamicFile = (file: File): ImageLightFileValidationError | null => {
  if (!isImageLightDynamicFile(file)) {
    return {
      errorKey: 'image_light_dynamic_type_error',
      context: 'unsupported dynamic image type',
      detail: { name: file.name, type: file.type },
    }
  }

  if (file.size > imageLightMaxDynamicFileSizeBytes) {
    return {
      errorKey: 'image_light_dynamic_too_large',
      context: 'dynamic image file is too large',
      detail: { name: file.name, size: file.size, maxSize: imageLightMaxDynamicFileSizeBytes },
    }
  }

  return null
}
